import React, { useEffect, useState } from "react";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useAssetTypes from "../../../../store/ReactQueries/assets/assetTypes/useAssetTypes";
import useAddAssetTypes from "../../../../store/ReactQueries/assets/assetTypes/useAddAssetType";
import useUpdateAssetType from "../../../../store/ReactQueries/assets/assetTypes/useUpdateAssetType";
import useDeleteAssetType from "../../../../store/ReactQueries/assets/assetTypes/useDeleteAssetType";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../utils/constants";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { formatDate } from "../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { AssetTypeInterface } from "../../../../models/AssetModel/AssetType";
import { assetTypesSchema } from "../../../../domain/ValidationSchemas/Asset/assetType";

export const AssetTypes = () => {

    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<AssetTypeInterface>({
        id: "",
        title: "",
    });

    const [modal, setModal] = useState<{
        add: boolean;
        edit: boolean;
        view: boolean;
    }>({
        add: false,
        edit: false,
        view: false,
    });

    let { data: assetTypes, error } = useAssetTypes(page, pageSize);

    const addAssetType = useAddAssetTypes(page, pageSize);
    const updateAssetType = useUpdateAssetType(page, pageSize);
    const deleteAssetType = useDeleteAssetType(page, pageSize);

    useEffect(() => {
        if (error) ToastMessage.Failed(`Error: ${error.message}`);
    }, [error]);

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            type: "string",
            editable: false,
            width: 200,
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            editable: true,
            width: 250,
        },
        {
            field: "createdDate",
            headerName: "CreatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
        {
            field: "updatedDate",
            headerName: "UpdatedDate",
            type: "dateTime",
            editable: false,
            width: 150,
            valueFormatter: formatDate,
        },
    ];

    const addItems = () => {
        setForm({
            ...form,
            title: "",
            id: "",
        });
        setModal({ ...modal, add: true });
        setErrors({});
    };

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleSave = async () => {
        const isValid = validateSchema(assetTypesSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: AssetTypeInterface = {
            id: form.id,
            title: form.title,
        };

        if (upForm.id === "") {
            const { id, ...formData } = upForm;
            addAssetType.mutate(formData, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        } else {
            updateAssetType.mutate(upForm, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }

        setModal({ ...modal, add: false, edit: false, view: false });
    };

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
            deleteAssetType.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language["AssetTypes"]}
            rows={assetTypes || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            setModal={setModal}
            addItems={addItems}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            showColumnModal={["title"]}
            hideColumns={["authorId", "id"]}
        />
    );
};
export default AssetTypes;
